/**
 * Choosing where a first-time player wakes up.
 *
 * The rules live in `shared/tutorial.ts`; this is the part that has to walk the
 * galaxy to apply them, which is why it is here rather than there — the
 * generator sits above the tutorial in the dependency order, and passing a
 * shortlist in keeps the rules testable without building a galaxy to test them.
 */

import {
  getGalaxyMap,
  getSystem,
  isCoreSystem,
  pickTutorialStart,
  type TutorialStart,
} from '@interspacial/shared';

/**
 * Systems in the core that a starter hull could actually land in.
 *
 * The small-pad requirement is the whole point: the starter is small, and
 * dropping somebody beside a station whose berths it cannot use is not a
 * tutorial, it is a wall.
 *
 * Roughly sixty candidates on the dev seed — few enough that new players are
 * near each other and can meet without crossing the galaxy, which is the reason
 * the core fraction is as tight as it is.
 */
export function tutorialCandidates(seed: string): TutorialStart[] {
  const map = getGalaxyMap(seed);
  const systems = map.flatMap((entry) => entry.systems);

  const radius = Math.max(
    ...systems.map((summary) =>
      Math.hypot(
        summary.galacticPosition.x,
        summary.galacticPosition.y,
        summary.galacticPosition.z,
      ),
    ),
  );

  const found: TutorialStart[] = [];

  for (const summary of systems) {
    const distance = Math.hypot(
      summary.galacticPosition.x,
      summary.galacticPosition.y,
      summary.galacticPosition.z,
    );
    if (!isCoreSystem(distance, radius)) continue;
    // Cheap check before the expensive one: generating a system is far dearer
    // than reading the count the map already carries.
    if (summary.stationCount === 0) continue;

    for (const station of getSystem(seed, summary.id).stations) {
      const pad = station.pads.find((berth) => berth.size === 'small');
      if (!pad) continue;

      found.push({ systemId: summary.id, stationId: station.id, padId: pad.id });
      break;
    }
  }

  return found;
}

/**
 * Where this account starts, and the berth it is being talked into.
 *
 * Genuinely random, not seeded off the player. It used to be the latter so a
 * replay was the same tutorial, which is a real property and not the one worth
 * having: it also bound an account permanently to one station, so replaying the
 * introduction always went back to the same berth.
 *
 * Rolled once and then written into the account's berth, so it is stable for
 * the run being flown — the randomness is at the moment of assignment, not on
 * every read.
 */
export function tutorialStartFor(seed: string): TutorialStart | null {
  return pickTutorialStart(tutorialCandidates(seed), { float: Math.random });
}
